import { customerSkillSearchText, customerSkillText } from "./skillPresentation";

// Client-facing presentation helpers for the authoritative skill registry and
// skill-change feed returned by `GET /api/skills`.
//
// These helpers only reshape and label values that the server already supplied.
// Nothing is inferred from names. An unknown action is humanized rather than
// replaced with a friendlier guess; a promotion or drift token with no client
// meaning (the ledger defaults `needs_human_review` and `none`) shows nothing.

export const BULK_UPDATE_THRESHOLD = 3;

const ACTION_LABELS = Object.freeze({
  created: "Added",
  create: "Added",
  installed: "Installed",
  install: "Installed",
  updated: "Improved",
  update: "Improved",
  patch: "Improved",
  sync: "Updated by GrowthOS",
  upgraded: "Upgraded",
  promoted: "Shared with GrowthOS",
  rollback: "Rolled back",
  rolled_back: "Rolled back",
  deleted: "Removed",
  removed: "Removed",
  delete: "Removed",
});

const PROMOTION_LABELS = Object.freeze({
  promoted: "Now a shared GrowthOS skill",
  candidate: "Proposed as a shared GrowthOS skill",
  pending: "Waiting on a GrowthOS review",
});

const DRIFT_LABELS = Object.freeze({
  drifted: "Customized away from its shared version",
  aligned: "Matches its shared version",
  pending_review: "Waiting on a review",
});

function normalizeToken(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function humanize(value) {
  const token = normalizeToken(value);
  if (!token) return null;
  return token.replaceAll("_", " ").replace(/^\w/, (letter) => letter.toUpperCase());
}

// Good news reads green wherever an action word appears: a skill improved, added, installed or shared.
const GOOD_ACTIONS = new Set(["created", "create", "installed", "install", "updated", "update", "patch", "sync", "upgraded", "promoted"]);
const BAD_ACTIONS = new Set(["deleted", "delete", "removed"]);

export function actionTone(action) {
  const token = normalizeToken(action);
  if (GOOD_ACTIONS.has(token)) return "good";
  if (BAD_ACTIONS.has(token)) return "warn";
  return "neutral";
}

export function formatActionLabel(action) {
  const token = normalizeToken(action);
  if (!token) return "Changed";
  return ACTION_LABELS[token] ?? humanize(token);
}

export function formatPromotionLabel(promotionStatus) {
  const token = normalizeToken(promotionStatus);
  if (!token) return null;
  return PROMOTION_LABELS[token] ?? null;
}

export function formatDriftLabel(driftStatus) {
  const token = normalizeToken(driftStatus);
  if (!token) return null;
  return DRIFT_LABELS[token] ?? null;
}

export function parseTimestamp(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function dayKey(value) {
  const parsed = parseTimestamp(value);
  if (!parsed) return null;
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
}

export function formatDayLabel(value, now = new Date()) {
  const parsed = parseTimestamp(value);
  if (!parsed) return "Date not reported";
  const today = dayKey(now);
  const yesterday = dayKey(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  const key = dayKey(parsed);
  if (key === today) return "Today";
  if (key === yesterday) return "Yesterday";
  return parsed.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function formatExactDate(value) {
  const parsed = parseTimestamp(value);
  if (!parsed) return "Date not reported";
  return parsed.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** "Last used today", "Last used yesterday" or "Last used" and the date; null when no readable time was reported. */
export function formatLastUsed(value, now = new Date()) {
  const parsed = parseTimestamp(value);
  if (!parsed) return null;
  const label = formatDayLabel(parsed, now);
  return `Last used ${label === "Today" || label === "Yesterday" ? label.toLowerCase() : label}`;
}

/** "1 use", "18 uses"; null unless a positive count was reported. */
export function formatUseCount(count) {
  return count > 0 ? `${count} ${count === 1 ? "use" : "uses"}` : null;
}

// A repaired or backfilled registry can emit many identical-timestamp events at
// once. Those are collapsed into one reviewable batch so a single maintenance
// pass cannot look like a day of independent work.
export function groupSkillChanges(changes, { bulkThreshold = BULK_UPDATE_THRESHOLD, now = new Date() } = {}) {
  const usable = (changes ?? [])
    .filter((change) => parseTimestamp(change?.occurredAt))
    .sort((left, right) => parseTimestamp(right.occurredAt) - parseTimestamp(left.occurredAt));

  const days = [];
  const dayIndex = new Map();
  for (const change of usable) {
    const key = dayKey(change.occurredAt);
    if (!dayIndex.has(key)) {
      const day = { key, label: formatDayLabel(change.occurredAt, now), occurredAt: change.occurredAt, changes: [] };
      dayIndex.set(key, day);
      days.push(day);
    }
    dayIndex.get(key).changes.push(change);
  }

  return days.map((day) => {
    const clusters = new Map();
    for (const change of day.changes) {
      const clusterKey = `${change.occurredAt}|${normalizeToken(change.action)}|${normalizeToken(change.scope)}`;
      if (!clusters.has(clusterKey)) clusters.set(clusterKey, []);
      clusters.get(clusterKey).push(change);
    }
    const entries = [];
    for (const [clusterKey, clusterChanges] of clusters) {
      if (clusterChanges.length >= bulkThreshold) {
        entries.push({
          type: "batch",
          key: clusterKey,
          action: clusterChanges[0].action,
          scope: clusterChanges[0].scope,
          occurredAt: clusterChanges[0].occurredAt,
          count: clusterChanges.length,
          changes: clusterChanges,
        });
      } else {
        for (const change of clusterChanges) entries.push({ type: "event", key: change.id ?? clusterKey, change });
      }
    }
    return { key: day.key, label: day.label, occurredAt: day.occurredAt, total: day.changes.length, entries };
  });
}

function changesSince(changes, days, now) {
  const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000;
  return (changes ?? []).filter((change) => {
    const parsed = parseTimestamp(change?.occurredAt);
    return parsed !== null && parsed.getTime() >= cutoff;
  });
}

export function countUpdatesSince(changes, { days = 7, now = new Date() } = {}) {
  return changesSince(changes, days, now).length;
}

// The newest reported change, or null when none carries a readable time.
export function latestChangeAt(changes) {
  let latest = null;
  for (const change of changes ?? []) {
    const parsed = parseTimestamp(change?.occurredAt);
    if (parsed && (!latest || parsed > latest)) latest = parsed;
  }
  return latest;
}

/**
 * How many changes landed on each of the last `days` calendar days, oldest first, for a small bar chart:
 * [{ key, label, count, today, date }], where label is the weekday's initial.
 */
export function dailyCounts(changes, { days = 7, now = new Date() } = {}) {
  const counts = new Map();
  for (const change of changes ?? []) {
    const key = dayKey(change?.occurredAt);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const todayKey = dayKey(now);
  return Array.from({ length: days }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1 - index));
    const key = dayKey(date);
    return {
      key,
      label: date.toLocaleDateString(undefined, { weekday: "narrow" }),
      date: date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }),
      count: counts.get(key) ?? 0,
      today: key === todayKey,
    };
  });
}

/** What kind of changes the last `days` days held, most common first: [{ label, count }] ("Improved", 9). */
export function actionBreakdown(changes, { days = 7, now = new Date() } = {}) {
  const counts = new Map();
  for (const change of changesSince(changes, days, now)) {
    const label = formatActionLabel(change.action);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return Array.from(counts, ([label, count]) => ({ label, count })).sort((left, right) => right.count - left.count);
}

export function matchesSkillSearch(skill, search) {
  const term = String(search ?? "").trim().toLowerCase();
  if (!term) return true;
  return [customerSkillSearchText(skill), customerSkillText(skill?.whatChanged), customerSkillText(skill?.whyChanged)]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(term);
}
